import { STREAM_STYLE_LABELS, STREAM_STYLE_IDS, streamStyleRecord } from './stream-themes';
import { CheckCircle2, Plus, RefreshCw, Search, Trash2, UserRoundCheck, Volume2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { StreamEffectStyle } from './stream-api';
import {
  deleteWelcomeProfile,
  loadWelcomes,
  saveWelcomeDefaults,
  saveWelcomeFallback,
  saveWelcomeProfile,
  type WelcomeEffectsSnapshot,
  type WelcomeProfile,
  type WelcomeProfilesSnapshot,
} from './welcomes-api';
import './welcomes.css';

const STYLE_LABELS = STREAM_STYLE_LABELS;

type EditorDraft = {
  login: string;
  enabled: boolean;
  soundId: string;
  styles: StreamEffectStyle[];
  chatText: string;
  displayOverrides: Record<StreamEffectStyle, string>;
  chatOverrides: Record<StreamEffectStyle, string>;
};

function emptyDraft(): EditorDraft {
  return {
    login: '',
    enabled: true,
    soundId: '',
    styles: [...STREAM_STYLE_IDS],
    chatText: '',
    displayOverrides: streamStyleRecord(() => ''),
    chatOverrides: streamStyleRecord(() => ''),
  };
}

function profileDraft(profile: WelcomeProfile): EditorDraft {
  return {
    login: profile.userLogin,
    enabled: profile.enabled,
    soundId: profile.soundId ?? '',
    styles: [...profile.styles],
    chatText: profile.chatText ?? '',
    displayOverrides: streamStyleRecord((id) => profile.displayOverrides[id] ?? ''),
    chatOverrides: streamStyleRecord((id) => profile.chatOverrides[id] ?? ''),
  };
}

function compactOverrides(values: Record<StreamEffectStyle, string>): Partial<Record<StreamEffectStyle, string>> {
  const result: Partial<Record<StreamEffectStyle, string>> = {};
  for (const style of Object.keys(values) as StreamEffectStyle[]) {
    const text = values[style].trim();
    if (text) result[style] = text;
  }
  return result;
}

export function WelcomesPage() {
  const [effects, setEffects] = useState<WelcomeEffectsSnapshot | null>(null);
  const [profiles, setProfiles] = useState<WelcomeProfilesSnapshot | null>(null);
  const [search, setSearch] = useState('');
  const [selectedLogin, setSelectedLogin] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<EditorDraft>(emptyDraft);
  const [returningText, setReturningText] = useState('');
  const [newViewerText, setNewViewerText] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const refresh = useCallback(async () => {
    try {
      const next = await loadWelcomes();
      setEffects(next.effects);
      setProfiles(next.profiles);
      setReturningText(next.effects.welcomeChatDefaults.returning);
      setNewViewerText(next.effects.welcomeChatDefaults.newViewer);
      setSelectedLogin((current) => {
        if (creating) return current;
        if (current && next.profiles.profiles.some((profile) => profile.userLogin === current)) return current;
        return next.profiles.profiles[0]?.userLogin ?? null;
      });
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Welcomes could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [creating]);

  useEffect(() => {
    void refresh();
  }, []);

  const selected = profiles?.profiles.find((profile) => profile.userLogin === selectedLogin) ?? null;

  useEffect(() => {
    if (creating) return;
    if (selected) setDraft(profileDraft(selected));
  }, [creating, selected?.userLogin, selected?.updatedAt]);

  const visibleProfiles = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('en-GB');
    return (profiles?.profiles ?? []).filter((profile) => !query || `${profile.displayName} ${profile.userLogin} ${profile.soundName ?? ''}`.toLocaleLowerCase('en-GB').includes(query));
  }, [profiles, search]);

  function beginCreate() {
    setCreating(true);
    setSelectedLogin(null);
    setDraft(emptyDraft());
    setMessage('');
  }

  function chooseProfile(profile: WelcomeProfile) {
    setCreating(false);
    setSelectedLogin(profile.userLogin);
    setDraft(profileDraft(profile));
    setMessage('');
  }

  function toggleStyle(style: StreamEffectStyle) {
    setDraft((current) => ({
      ...current,
      styles: current.styles.includes(style)
        ? current.styles.filter((item) => item !== style)
        : [...current.styles, style],
    }));
  }

  async function saveDefaults() {
    const returning = returningText.trim();
    const newViewer = newViewerText.trim();
    if (!returning || !newViewer) {
      setMessage('Both automatic welcome messages are required.');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      const next = await saveWelcomeDefaults(returning, newViewer);
      setEffects(next);
      setReturningText(next.welcomeChatDefaults.returning);
      setNewViewerText(next.welcomeChatDefaults.newViewer);
      setMessage('Automatic welcome messages saved.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Automatic welcome messages could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  async function saveProfile() {
    const login = draft.login.trim().replace(/^@+/, '');
    if (!login) {
      setMessage('Enter the Twitch login for this personalised welcome.');
      return;
    }
    if (draft.styles.length === 0) {
      setMessage('Choose at least one stream style.');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      const next = await saveWelcomeProfile(login, {
        soundId: draft.soundId || null,
        enabled: draft.enabled,
        styles: draft.styles,
        displayOverrides: compactOverrides(draft.displayOverrides),
        chatText: draft.chatText.trim() || null,
        chatOverrides: compactOverrides(draft.chatOverrides),
      });
      setProfiles(next);
      const saved = next.profiles.find((profile) => profile.userLogin === login.toLocaleLowerCase('en-GB'))
        ?? next.profiles.find((profile) => profile.userId && profile.userId === selected?.userId)
        ?? null;
      setCreating(false);
      setSelectedLogin(saved?.userLogin ?? login.toLocaleLowerCase('en-GB'));
      if (saved) setDraft(profileDraft(saved));
      setMessage(`Personalised welcome for ${saved?.displayName ?? login} saved.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Personalised welcome could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  async function removeProfile() {
    if (!selected || !window.confirm(`Delete the personalised welcome for ${selected.displayName}?`)) return;
    setBusy(true);
    setMessage('');
    try {
      const next = await deleteWelcomeProfile(selected.userLogin);
      setProfiles(next);
      setSelectedLogin(next.profiles[0]?.userLogin ?? null);
      setCreating(false);
      setMessage(`Personalised welcome for ${selected.displayName} deleted.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Personalised welcome could not be deleted.');
    } finally {
      setBusy(false);
    }
  }

  async function changeFallback(style: StreamEffectStyle, soundId: string | null) {
    setBusy(true);
    setMessage('');
    try {
      const next = await saveWelcomeFallback(style, soundId);
      setEffects(next);
      setMessage(`${STYLE_LABELS[style]} fallback welcome sound ${soundId ? 'updated' : 'cleared'}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Fallback welcome sound could not be changed.');
    } finally {
      setBusy(false);
    }
  }

  if (loading && !effects) return <div className="ffs-next-loading">Loading Welcomes…</div>;

  return (
    <div className="ffs-workspace-page ffs-welcomes-page">
      {message ? <div className="ffs-workspace-message">{message}</div> : null}

      <section className="ffs-workspace-hero">
        <div>
          <small>VIEWER WELCOME RULES</small>
          <h2>Welcomes</h2>
          <p>Automatic defaults first; personalised exceptions only where someone needs different sound, text or presentation.</p>
        </div>
        <button type="button" className="ffs-button secondary" disabled={busy} onClick={() => void refresh()}><RefreshCw size={15} /> Refresh</button>
      </section>

      <section className="ffs-welcome-default-grid">
        <article className="ffs-workspace-card ffs-welcome-defaults-card">
          <header><div><small>AUTOMATIC CHAT</small><h3>New &amp; returning viewers</h3></div><UserRoundCheck size={18} /></header>
          <p className="ffs-workspace-note">FFSControl decides new versus returning from persistent viewer history. A personalised chat message below replaces these defaults for that person.</p>
          <div className="ffs-welcome-default-fields">
            <label><span>Returning viewer</span><textarea rows={3} maxLength={1000} value={returningText} onChange={(event) => setReturningText(event.target.value)} disabled={busy} /></label>
            <label><span>New viewer <small>may contain multiple lines/messages</small></span><textarea rows={3} maxLength={1000} value={newViewerText} onChange={(event) => setNewViewerText(event.target.value)} disabled={busy} /></label>
          </div>
          <div className="ffs-inline-actions"><button type="button" className="ffs-button primary" disabled={busy || !returningText.trim() || !newViewerText.trim()} onClick={() => void saveDefaults()}>Save Default Messages</button></div>
        </article>

        <article className="ffs-workspace-card ffs-welcome-fallback-card">
          <header><div><small>DEFAULT AUDIO</small><h3>Fallback sounds by style</h3></div><Volume2 size={18} /></header>
          <p className="ffs-workspace-note">Used when a viewer does not have their own enabled personalised sound.</p>
          <div className="ffs-fallback-list">
            {(effects?.welcomeFallbacks ?? []).map((fallback) => (
              <div key={fallback.style} className={fallback.enabled ? 'ready' : fallback.configured ? 'disabled' : 'empty'}>
                <div><strong>{STYLE_LABELS[fallback.style]}</strong><span>{fallback.soundName ?? 'No fallback sound configured'}</span></div>
                <select value={fallback.soundId ?? ''} disabled={busy || Boolean(effects?.activeSession)} aria-label={`${STYLE_LABELS[fallback.style]} fallback welcome sound`} onChange={(event) => void changeFallback(fallback.style, event.target.value || null)}>
                  <option value="">No fallback sound</option>
                  {profiles?.sounds.map((sound) => <option value={sound.id} key={sound.id} disabled={!sound.enabled}>{sound.name}{sound.enabled ? '' : ' — disabled'}</option>)}
                </select>
                <small>{fallback.enabled ? 'READY' : fallback.configured ? 'SOUND DISABLED' : 'NOT CONFIGURED'}</small>
              </div>
            ))}
          </div>
          {effects?.activeSession ? <><div className="ffs-viewer-notice">End the active stream session before changing fallback welcome sounds.</div><div className="ffs-welcome-session-summary"><span>Current cycle</span><strong>{STYLE_LABELS[effects.activeSession.style]} · Welcome Cycle #{effects.activeSession.welcomeGeneration}</strong><small>{effects.activeSession.welcomes.queued} queued · {effects.activeSession.welcomes.playing} playing · {effects.activeSession.welcomes.welcomed} welcomed</small></div></> : <div className="ffs-viewer-notice">No active stream session. Welcome configuration is safe to edit here.</div>}
        </article>
      </section>

      <section className="ffs-welcome-profile-grid">
        <article className="ffs-workspace-card ffs-welcome-profile-list-card">
          <header><div><small>PERSONALISED EXCEPTIONS</small><h3>Viewer profiles</h3></div><button type="button" className="ffs-button primary" disabled={busy} onClick={beginCreate}><Plus size={14} /> New Welcome</button></header>
          <label className="ffs-search-field"><Search size={15} /><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search viewer, login or sound" /></label>
          <div className="ffs-welcome-profile-list">
            {visibleProfiles.map((profile) => (
              <button type="button" key={profile.userLogin} className={!creating && selectedLogin === profile.userLogin ? 'selected' : ''} onClick={() => chooseProfile(profile)}>
                <div><strong>{profile.displayName}</strong><span>@{profile.userLogin}</span><small>{profile.soundName ?? 'Uses fallback sound'} · {profile.styles.map((style) => STYLE_LABELS[style]).join(', ')}</small></div>
                <span className={profile.enabled ? 'enabled' : 'disabled'}>{profile.enabled ? 'ON' : 'OFF'}</span>
              </button>
            ))}
            {visibleProfiles.length === 0 ? <div className="ffs-empty-state">No personalised welcomes match this search.</div> : null}
          </div>
        </article>

        <article className="ffs-workspace-card ffs-welcome-editor-card">
          {creating || selected ? (
            <>
              <header>
                <div><small>{creating ? 'NEW PERSONALISED WELCOME' : 'PERSONALISED WELCOME'}</small><h3>{creating ? 'Add viewer' : selected?.displayName}</h3>{!creating && selected ? <p>@{selected.userLogin} · {selected.soundName ?? 'fallback sound'}</p> : null}</div>
                {!creating && selected ? <button type="button" className="ffs-button danger" disabled={busy} onClick={() => void removeProfile()}><Trash2 size={14} /> Delete</button> : null}
              </header>

              <div className="ffs-welcome-editor">
                <div className="ffs-welcome-editor-row">
                  <label><span>Twitch login</span><input value={draft.login} disabled={busy || !creating} onChange={(event) => setDraft((current) => ({ ...current, login: event.target.value }))} placeholder="viewer_login" /></label>
                  <label><span>Personal sound</span><select value={draft.soundId} disabled={busy} onChange={(event) => setDraft((current) => ({ ...current, soundId: event.target.value }))}><option value="">Use style fallback</option>{profiles?.sounds.map((sound) => <option value={sound.id} key={sound.id} disabled={!sound.enabled}>{sound.name}{!sound.enabled ? ' — disabled' : !sound.soundboardVisible ? ' — hidden from soundboard' : ''}</option>)}</select></label>
                  <label className="ffs-welcome-enabled-choice"><span>State</span><button type="button" className={`ffs-button ${draft.enabled ? 'primary' : 'secondary'}`} disabled={busy} onClick={() => setDraft((current) => ({ ...current, enabled: !current.enabled }))}>{draft.enabled ? <><CheckCircle2 size={14} /> Enabled</> : 'Disabled'}</button></label>
                </div>

                <fieldset className="ffs-style-choices"><legend>Active stream styles</legend>{(STREAM_STYLE_IDS as StreamEffectStyle[]).map((style) => <label key={style}><input type="checkbox" checked={draft.styles.includes(style)} disabled={busy} onChange={() => toggleStyle(style)} /><span>{STYLE_LABELS[style]}</span></label>)}</fieldset>

                <label><span>Personal chat welcome <small>blank = automatic new/returning default</small></span><textarea rows={3} maxLength={1000} value={draft.chatText} disabled={busy} onChange={(event) => setDraft((current) => ({ ...current, chatText: event.target.value }))} placeholder="Optional personal welcome text" /></label>

                <div className="ffs-welcome-style-overrides">
                  {(STREAM_STYLE_IDS as StreamEffectStyle[]).map((style) => (
                    <section key={style} className={!draft.styles.includes(style) ? 'inactive' : ''}>
                      <strong>{STYLE_LABELS[style]}</strong>
                      <label><span>Display-name override</span><input maxLength={250} value={draft.displayOverrides[style]} disabled={busy || !draft.styles.includes(style)} onChange={(event) => setDraft((current) => ({ ...current, displayOverrides: { ...current.displayOverrides, [style]: event.target.value } }))} placeholder="Use Twitch display name" /></label>
                      <label><span>Chat override</span><textarea rows={2} maxLength={1000} value={draft.chatOverrides[style]} disabled={busy || !draft.styles.includes(style)} onChange={(event) => setDraft((current) => ({ ...current, chatOverrides: { ...current.chatOverrides, [style]: event.target.value } }))} placeholder="Use personal/default chat text" /></label>
                    </section>
                  ))}
                </div>

                <div className="ffs-inline-actions"><button type="button" className="ffs-button primary" disabled={busy || !draft.login.trim() || draft.styles.length === 0} onClick={() => void saveProfile()}>{creating ? 'Create Personalised Welcome' : 'Save Changes'}</button>{creating ? <button type="button" className="ffs-button secondary" disabled={busy} onClick={() => { setCreating(false); setSelectedLogin(profiles?.profiles[0]?.userLogin ?? null); }}>Cancel</button> : null}</div>
              </div>
            </>
          ) : <div className="ffs-empty-state">Choose a personalised welcome or create a new one.</div>}
        </article>
      </section>
    </div>
  );
}
